import React from 'react';
import { Descriptions, Drawer, Image, Space, Tag } from 'antd';
import type { Product } from '../../../types/product';
import type { Platform172Detail } from '../../../api/external';
import { formatDate } from '../../../utils/dateUtils';

interface ProductDetailDrawerProps {
  open: boolean;
  product: Product | null;
  platform172Detail: Platform172Detail | null;
  zIndex?: number;
  onClose: () => void;
}

const sourcePlatformColors: Record<string, string> = {
  haoyi: 'blue',
  '172': 'orange',
  manual: 'green',
};

const sourcePlatformLabels: Record<string, string> = {
  haoyi: '号易',
  '172': '172平台',
  manual: '手动创建',
};

const operatorColors: Record<string, string> = {
  移动: 'blue',
  联通: 'green',
  电信: 'orange',
  广电: 'purple',
};

const duplicateStatusLabels: Record<string, string> = {
  none: '普通',
  candidate: '候选',
  confirmed: '已确认',
  ignored: '已忽略',
};

const duplicateStatusColors: Record<string, string> = {
  none: 'default',
  candidate: 'orange',
  confirmed: 'red',
  ignored: 'blue',
};

const ProductDetailDrawer: React.FC<ProductDetailDrawerProps> = ({
  open,
  product,
  platform172Detail,
  zIndex,
  onClose,
}) => {
  return (
    <Drawer
      title={`产品详情 - ${product?.name || ''}`}
      placement="right"
      width={640}
      onClose={onClose}
      open={open}
      zIndex={zIndex}
    >
      {product && (
        <>
          <Descriptions title="基本信息" bordered size="small" column={2}>
            <Descriptions.Item label="ID" span={1}>{product.id}</Descriptions.Item>
            <Descriptions.Item label="来源平台" span={1}>
              <Tag color={sourcePlatformColors[product.source_platform || 'manual']}>
                {sourcePlatformLabels[product.source_platform || 'manual']}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="产品名称" span={2}>{product.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="副标题" span={2}>{product.subtitle || '-'}</Descriptions.Item>
            <Descriptions.Item label="运营商" span={1}>
              <Tag color={operatorColors[product.operator || ''] || 'default'}>
                {product.operator || '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="分类" span={1}>{product.category || '-'}</Descriptions.Item>
            <Descriptions.Item label="状态" span={1}>
              <Tag color={product.status === '在售' ? 'green' : 'red'}>{product.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="外部状态" span={1}>
              <Tag color={product.external_status === '在售' ? 'blue' : 'red'}>{product.external_status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="客户端展示" span={1}>
              <Tag color={product.is_hidden_from_client ? 'red' : 'green'}>
                {product.is_hidden_from_client ? '隐藏' : '可见'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="重复状态" span={1}>
              <Tag color={duplicateStatusColors[product.duplicate_status || 'none'] || 'default'}>
                {duplicateStatusLabels[product.duplicate_status || 'none'] || product.duplicate_status || '普通'}
              </Tag>
            </Descriptions.Item>
            {product.duplicate_reason && (
              <Descriptions.Item label="重复说明" span={2}>{product.duplicate_reason}</Descriptions.Item>
            )}
          </Descriptions>

          <Descriptions title="资费套餐" bordered size="small" column={2} style={{ marginTop: 16 }}>
            <Descriptions.Item label="原月租" span={1}>{product.monthly_original ? `${product.monthly_original}元` : '-'}</Descriptions.Item>
            <Descriptions.Item label="当前月租" span={1}>{product.monthly_current ? `${product.monthly_current}元` : '-'}</Descriptions.Item>
            <Descriptions.Item label="通用流量" span={1}>{product.traffic_general || '-'}</Descriptions.Item>
            <Descriptions.Item label="定向流量" span={1}>{product.traffic_directional || '-'}</Descriptions.Item>
            <Descriptions.Item label="通话时长" span={1}>{product.voice || '-'}</Descriptions.Item>
            <Descriptions.Item label="首充规则" span={2}>{product.charge_rule || '-'}</Descriptions.Item>
            <Descriptions.Item label="套餐说明" span={2}>{product.package_note || '-'}</Descriptions.Item>
          </Descriptions>

          <Descriptions title="佣金结算" bordered size="small" column={2} style={{ marginTop: 16 }}>
            <Descriptions.Item label="面值" span={1}>{product.face_value ? `${product.face_value}元` : '-'}</Descriptions.Item>
            <Descriptions.Item label="结算价" span={1}>{product.settle_price ? `¥${product.settle_price}` : '-'}</Descriptions.Item>
            <Descriptions.Item label="返佣类型" span={1}>{product.commission_type || '-'}</Descriptions.Item>
            <Descriptions.Item label="结算规则" span={2}>{product.settle_rule || '-'}</Descriptions.Item>
            <Descriptions.Item label="一级佣金" span={1}>{product.commission_level1 || '-'}</Descriptions.Item>
            <Descriptions.Item label="二级佣金" span={1}>{product.commission_level2 || '-'}</Descriptions.Item>
            <Descriptions.Item label="三级佣金" span={1}>{product.commission_level3 || '-'}</Descriptions.Item>
          </Descriptions>

          <Descriptions title="限制规则" bordered size="small" column={2} style={{ marginTop: 16 }}>
            <Descriptions.Item label="年龄限制" span={1}>
              {product.age_min != null && product.age_max != null
                ? `${product.age_min}~${product.age_max}岁`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="合约期" span={1}>{product.contract_period || '-'}</Descriptions.Item>
            <Descriptions.Item label="可办地区" span={2}>
              {product.shippingPolicy?.display || product.derivedShippingScope?.display || (
                Array.isArray(product.forbidden_regions) && product.forbidden_regions.length > 0
                ? <Space wrap>{product.forbidden_regions.map((region, index) => <Tag key={index} color="red">{region}</Tag>)}</Space>
                : '-'
              )}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions title="配送" bordered size="small" column={2} style={{ marginTop: 16 }}>
            <Descriptions.Item label="需要发货" span={1}>{product.need_shipping ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="快递方式" span={1}>{product.express_method || '-'}</Descriptions.Item>
          </Descriptions>

          <Descriptions title="下单配置" bordered size="small" column={2} style={{ marginTop: 16 }}>
            <Descriptions.Item label="需身份证号" span={1}>{product.need_id_card ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="需身份证图片" span={1}>{product.need_id_card_pic ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="需人像照" span={1}>{product.need_face_pic ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="需验证码" span={1}>{product.need_verify_code ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="需在线付款" span={1}>{product.need_payment ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="支持选号" span={1}>{product.support_number_selection ? '是' : '否'}</Descriptions.Item>
          </Descriptions>

          <Descriptions title="激活" bordered size="small" style={{ marginTop: 16 }}>
            <Descriptions.Item label="激活方式">{product.activation_method || '-'}</Descriptions.Item>
            {product.activation_flow_image && product.activation_flow_image !== '无' && (
              <Descriptions.Item label="激活流程图">
                <Image src={product.activation_flow_image} width={200} />
              </Descriptions.Item>
            )}
            <Descriptions.Item label="激活指引">{product.activation_guide || '-'}</Descriptions.Item>
          </Descriptions>

          {product.image_main && (
            <Descriptions title="素材" bordered size="small" column={2} style={{ marginTop: 16 }}>
              <Descriptions.Item label="主图" span={2}>
                <Image src={product.image_main} width={80} />
              </Descriptions.Item>
              {product.image_detail && (
                <Descriptions.Item label="详情图" span={2}>
                  <Image src={product.image_detail} width={80} />
                </Descriptions.Item>
              )}
            </Descriptions>
          )}

          <Descriptions title="运营" bordered size="small" column={2} style={{ marginTop: 16 }}>
            <Descriptions.Item label="关键词" span={1}>{product.keywords || '-'}</Descriptions.Item>
            <Descriptions.Item label="主推" span={1}>{product.is_featured ? <Tag color="red">是</Tag> : '否'}</Descriptions.Item>
            <Descriptions.Item label="宽带产品" span={1}>{product.is_broadband ? <Tag color="blue">是</Tag> : '否'}</Descriptions.Item>
            <Descriptions.Item label="标签" span={1}>
              {Array.isArray(product.tags) ? product.tags.map((tag, index) => <Tag key={index}>{tag}</Tag>) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="备注" span={2}>{product.remark || '-'}</Descriptions.Item>
            <Descriptions.Item label="销量" span={1}>{product.sales}</Descriptions.Item>
            <Descriptions.Item label="库存" span={1}>{product.inventory}</Descriptions.Item>
          </Descriptions>

          <Descriptions title="外部平台关联" bordered size="small" style={{ marginTop: 16 }}>
            <Descriptions.Item label="来源平台">{product.source_platform || '-'}</Descriptions.Item>
            <Descriptions.Item label="外部ID">{product.source_product_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="最后同步">{product.last_synced_at ? formatDate(product.last_synced_at) : '-'}</Descriptions.Item>
          </Descriptions>

          {platform172Detail && (
            <Descriptions title="172 HTML详情" bordered size="small" column={1} style={{ marginTop: 16 }}>
              <Descriptions.Item label="页面标题">{platform172Detail.title}</Descriptions.Item>
              <Descriptions.Item label="产品套餐">{platform172Detail.productPackage}</Descriptions.Item>
              <Descriptions.Item label="原套餐说明">{platform172Detail.originalPlan}</Descriptions.Item>
              {Object.entries(platform172Detail.detailSections || {}).map(([key, value]) => (
                <Descriptions.Item key={key} label={key}>{value || '-'}</Descriptions.Item>
              ))}
              {platform172Detail.detailImg && (
                <Descriptions.Item label="详情图">
                  <Image src={platform172Detail.detailImg} width={200} />
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
        </>
      )}
    </Drawer>
  );
};

export default ProductDetailDrawer;
